import React, { useState, useEffect } from "react";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import './SignupPage.css';
import '../../assets/styles/form.css';

import editIcon from '../../assets/images/edit-icon.svg';

import LogoHeader from "../../components/LogoHeader";
import { API_BASE_URL, DEFAULT_PROFILE_PICTURE } from "../../Variables";

/**
 * @returns - the sign up page
 */
function SignupPage() {

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/home'); // Redirect to home page if logged in
    }
  }, [navigate]);

  const [userData, setUserData] = useState({
    username: '',
    display_name: '',
    email: '',
    password: '',
    profile_picture: '',
    bio: '',
    profile_visibility: 'private',
  });

  const [error, setError] = useState('');
  const [valid, setValid] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const defaultProfilePicture = `${API_BASE_URL}${DEFAULT_PROFILE_PICTURE}`;

  /**
   * Function to handle image change
   * Should redisplay the new image
   * 
   * - Need to handle image storage in backend
   * @param {*} e - event
   */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
  
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
  
      reader.readAsDataURL(file);
    }
  };

  /**
   * Validate password:
   * 1. At least one uppercase letter
   * 2. At least one lowercase letter
   * 3. At least one number
   * 4. At least one special character
   * 5. At least 8 characters long
   * 
   * @param {*} password 
   * @returns 
   */
  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  /**
   * Validate email format
   * @param {*} email - email to validate
   * @returns - true if email is valid, false otherwise
   */
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * This function will handle the submission of the first part of the form
   * It will check if all fields are filled, if the email and password is in the correct format
   * It will check if the username and email are unique
   * It will change the form to the second part if all checks pass
   * @param {*} e - event
   * @returns
   */
  const handleContinue = async(e) => {
    e.preventDefault();

    // check if all fields are filled
    if (!userData.username || !userData.email || !userData.password) {
      setError('All fields are required');
      setValid(false);
      return;
    }

    // check if email format is valid
    if (!validateEmail(userData.email)) {
      setError('Invalid email format');
      setValid(false);
      return;
    }
    
    // check if password format is valid
    if (!validatePassword(userData.password)) {
      setError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      setValid(false);
      return;
    }

    try {
      // Check if username is unique
      const usernameResponse = await axios.get(`${API_BASE_URL}/users/check-username/${userData.username}`);
      if (!usernameResponse.data.isUnique) {
        setError('Username is already taken');
        setValid(false);
        return;
      }

      // Check if email is unique
      const emailResponse = await axios.get(`${API_BASE_URL}/users/check-email/${userData.email}`);
      if (!emailResponse.data.isUnique) {
        setError('Email is already taken');
        setValid(false);
        return;
      }

      setUserData({ ...userData, display_name: userData.username });
      setError('');
      setValid(true);

    } catch (error) {
      setError('There was an error checking if the username or email is unique');
      setValid(false);
    }
  };

  /**
   * Handles the submission of the form to sign up the user
   * @returns 
   */
  const handleSubmit = () => {
    if (!userData.display_name) {
      setError("Display name is required");
      return;
    }
  
    const formData = new FormData();
  
    for (const [key, value] of Object.entries(userData)) {
      formData.append(key, value);
    }

    if (selectedImage) {
      formData.append("profile_picture", selectedImage); // Use the actual File object
    }
  
    // Submit the form data to the server
    axios.post(`${API_BASE_URL}/auth/create`, formData, {
        headers: {"Content-Type": "multipart/form-data"},
      })
      .then((response) => {
        setError("");
        navigate("/login");
      })
      .catch((error) => {
        setError("There was an error creating the user");
      });
  };

  /**
   * @returns - Display the first part of the form
   */
  const signUpForm = () => {
    return (
      
      <div className="form-container">
        <div className="form-input">
          <label htmlFor="username">Username</label>
          <input id="username" type="text" value={userData.username} onChange={(e) => setUserData({ ...userData, username: e.target.value })} />
        </div>
        <div className="form-input">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={userData.email} onChange={(e) => setUserData({ ...userData, email: e.target.value })} />
        </div>
        <div className="form-input">
          <label htmlFor="password">Password</label>
          <input  id="password" type="password" value={userData.password} onChange={(e) => setUserData({ ...userData, password: e.target.value })} />
        </div>
        <div className="form-input">
          <button onClick={handleContinue} className="primary-button">Sign Up</button>
        </div>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  /**
   * @returns - Display the second part of the form
   */
  const signUpFormContinue = () => {
    return (
      <div className="form-container">
        <div className="image-upload-container">
          <input
            type="file"
            id="profile-picture"
            onChange={handleImageChange}
            hidden
          />
          <label htmlFor="profile-picture" className="image-label">
            <div className="signup-profile-picture" >
              <img src={imagePreview || defaultProfilePicture} alt="Profile" className="profile-picture"/>
              <div className="edit-overlay"><img src={editIcon} className="edit-icon" alt="Edit pen" /></div>
            </div>
          </label>
        </div>
        <div className="form-input">
          <label htmlFor="display-name">Display Name</label>
          <input id="display-name" type="text" value={userData.display_name} onChange={(e) => setUserData({ ...userData, display_name: e.target.value })} />
        </div>
        <div className="form-input">
          <label htmlFor="bio">Bio</label>
          <input id="bio" type="text" value={userData.bio} onChange={(e) => setUserData({ ...userData, bio: e.target.value })} placeholder="optional"/>
        </div>
        <div className="form-input">
          <label>Profile Visibility</label>
          <select className="form-select" value={userData.profile_visibility} onChange={(e) => setUserData({ ...userData, profile_visibility: e.target.value })}>
            <option value="private" className="form-option">Private</option>
            <option value="public" className="form-option">Public</option>
          </select>
        </div>
        <div className="form-input">
          <button onClick={handleSubmit} className="primary-button">Sign Up</button>
        </div>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="signup-page">
      <LogoHeader className="signup-logo"/>
      <h2>Your own virtual book nook. Sign up now!</h2>
      {!valid ? signUpForm(): signUpFormContinue()}
      Already have an account? <a href="/login">Log in here</a>
    </div>
  );
}

export default SignupPage;
